import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError } from '../../../lib/http/ApiError';
import { useFetch } from '../../../hooks/useFetch';
import { useNotification } from '../../notifications/hooks/useNotification';
import { createUsersApi } from '../api/usersApi';
import type { User, UserStatusCounts, UsersQueryParams } from '../userTypes';
import type { PaginationMeta } from '../../../components/ui/tableTemplate/TableTemplateTypes';

interface UseGetUsersResult {
  data: User[] | null;
  meta: PaginationMeta | null;
  /** null until the first response, then kept while a reload is in flight. */
  counts: UserStatusCounts | null;
  /**
   * The `query` of the request the held `data` / `meta` / `counts` answer ('' = no search).
   * Unchanged while a reload is pending, fails or is aborted — so a caller can tell whether the
   * held counts are the organisation's (no search) or narrowed by a search.
   */
  responseQuery: string;
  loading: boolean;
  error: ApiError | Error | null;
  refetch: () => void;
}

/**
 * Query hook pour la liste paginée des utilisateurs (HOMCO-643 : `{data, meta}`, HOMCO-1357 : `counts`).
 * Les params sont sérialisés en clé stable — le re-fetch se déclenche quand ils
 * changent, sans exiger un objet référentiellement stable côté caller.
 *
 * Migration TanStack Query : remplacer useState/useEffect par useQuery.
 * createUsersApi et useFetch restent identiques.
 *
 *   return useQuery({
 *     queryKey: ['users', params],
 *     queryFn: ({ signal }) => api.getAll(params, { signal }),
 *   });
 */
export function useGetUsers(params?: UsersQueryParams): UseGetUsersResult {
  const [client, loading] = useFetch();
  const api = useMemo(() => createUsersApi(client), [client]);
  const { notifyError } = useNotification();

  const [data, setData] = useState<User[] | null>(null);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [counts, setCounts] = useState<UserStatusCounts | null>(null);
  const [responseQuery, setResponseQuery] = useState('');
  const [error, setError] = useState<ApiError | Error | null>(null);

  const paramsKey = JSON.stringify(params ?? {});

  const load = useCallback(
    async (signal?: AbortSignal) => {
      try {
        setError(null);
        const requested = JSON.parse(paramsKey) as UsersQueryParams;
        const res = await api.getAll(requested, { signal });
        // The effect cleanup aborted this request while its response was already read: it was
        // replaced by a newer one, so this answer must not overwrite the newer data.
        if (signal?.aborted) return;
        setData(res.data);
        setMeta(res.meta);
        setCounts(res.counts ?? null);
        setResponseQuery(requested.query ?? '');
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        const e = err as ApiError | Error;
        setError(e);
        // 5xx / réseau : notification globale — le user ne peut pas agir
        if (ApiError.isApiError(e) && e.isServerError) notifyError(e);
        else if (!ApiError.isApiError(e)) notifyError(e);
      }
    },
    [api, notifyError, paramsKey],
  );

  useEffect(() => {
    const ctrl = new AbortController();
    // eslint-disable-next-line react-hooks/set-state-in-effect -- setState is called async (post-await), not synchronously
    void load(ctrl.signal);
    return () => ctrl.abort();
  }, [load]);

  return { data, meta, counts, responseQuery, loading, error, refetch: () => void load() };
}
